import { Button, Space, Typography, Tooltip } from 'antd';
import { PlusOutlined, CalendarOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { KpiSummaryCards } from './KpiSummaryCards';

const { Title } = Typography;

interface EquipmentStatistics {
  total: number;
  active: number;
  highRisk: number;
  maintenanceRequired: number;
}

interface DashboardHeaderProps {
  companyName?: string;
  headerImageUrl?: string;
  hasCustomHeaderImage: boolean;
  shouldShowDefaultHeaderImage: boolean;
  statistics?: EquipmentStatistics;
  statisticsLoading?: boolean;
  isManagerOrAdmin?: boolean;
}

const headerImageDefaultSrc = '/images/factory/factory_header.png';

export function DashboardHeader({
  companyName,
  headerImageUrl,
  hasCustomHeaderImage,
  shouldShowDefaultHeaderImage,
  statistics,
  statisticsLoading,
  isManagerOrAdmin,
}: DashboardHeaderProps) {
  const navigate = useNavigate();

  const headerImageStyle: React.CSSProperties = {
    width: '100%',
    height: '420px',
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    filter: 'brightness(0.85)',
  };

  return (
    <div style={{ position: 'relative', marginBottom: 24 }}>
      {hasCustomHeaderImage ? (
        <img
          src={headerImageUrl}
          alt="工場の写真"
          style={headerImageStyle}
        />
      ) : shouldShowDefaultHeaderImage ? (
        <img
          src={headerImageDefaultSrc}
          alt="工場の写真"
          style={headerImageStyle}
        />
      ) : (
        <div
          style={{
            width: headerImageStyle.width,
            height: headerImageStyle.height,
            borderRadius: headerImageStyle.borderRadius,
            boxShadow: headerImageStyle.boxShadow,
            background: 'linear-gradient(135deg, rgba(24,144,255,0.15), rgba(24,144,255,0.05))',
          }}
        />
      )}

      <Space
        style={{
          position: 'absolute',
          top: '30px',
          left: '40px',
        }}
        align="center"
      >
        <Title
          level={2}
          style={{
            color: 'white',
            margin: 0,
            textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
            fontSize: '36px',
            fontWeight: 'bold',
          }}
        >
          {companyName || '会社ダッシュボード'}
        </Title>
        {isManagerOrAdmin && (
          <Tooltip title="詳細設定">
            <Button
              type="text"
              icon={<SettingOutlined style={{ fontSize: '20px' }} />}
              onClick={() => navigate('/app/settings/advanced')}
              style={{
                color: 'white',
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              }}
            />
          </Tooltip>
        )}
      </Space>

      {/* クイックアクションボタン */}
      <Space
        style={{
          position: 'absolute',
          top: '30px',
          right: '40px',
          gap: '12px',
        }}
        size="middle"
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate('/app/equipments')}
          style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          }}
        >
          設備管理
        </Button>
        <Button
          type="default"
          icon={<CalendarOutlined />}
          size="large"
          onClick={() => navigate('/app/maintenance')}
          style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#1f1f1f',
            borderColor: 'rgba(255, 255, 255, 0.9)',
          }}
        >
          保全カレンダー
        </Button>
      </Space>

      {/* KPIサマリーカードオーバーレイ */}
      <div
        className="dashboard-hero-kpi"
        style={{
          position: 'absolute',
          bottom: '30px',
          left: '40px',
          right: '40px',
          background: 'rgba(255, 255, 255, 0.5)',
          padding: '12px',
          borderRadius: '10px',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        }}
      >
        <KpiSummaryCards statistics={statistics} loading={statisticsLoading} gutter={[8, 8]} />
      </div>
    </div>
  );
}
